import store from '@/store';
import { assign, keys, isArray } from 'lodash';
import { uid, Parser } from '@/utils';
import {
  Scope,
  Mpo,
  Iot,
  Zenith,
} from '@/api';

export { default as log } from './logger';
export { default as download } from './download';
export * from './path-simplification';
export { default } from './iot-data';

/**
 * @param {Route} to
 */
export async function fetchScope(to) {
  store.commit('START_LOADING');
  if (to.params.scope) {
    const { data } = await Scope.find(to.params.scope);
    return data;
  }

  return { };
}

/**
 * @param {Route} to
 * @param {Route} from
 * @param {Object} scope
 */
export async function fetchOrderBeforeEnter(to, from, _, scope, _field) {
  if (to.matched.some(r => r.meta.search)) return Promise.reject();
  if (!from || to.params.number !== from.params.number) {
    const fieldDefault = {
      Zenith: 'serviceNumber',
      Mpo: 'shipmentOrder',
    }[scope.system];

    const field = (
      fieldDefault
      || _field
      || keys(to.query).find(q => q !== 'org')
    );

    let evs = [];

    store.commit('START_LOADING');

    const canCallIot = ['IBM', 'KYNDRYL'].some((s) => {
      if (scope.name) {
        return scope.name.toLowerCase().match(s.toLowerCase());
      }

      return false;
    });

    const system = { Mpo, Zenith }[scope.system];
    let details = {};
    let events = {};
    let contents = {};
    let documents = {};
    let infoEvents = {};

    try {
      (
        [
          { value: details },
          { value: events },
          { value: contents },
          { value: documents },
          { value: infoEvents },
        ] = await Promise.allSettled([
          system.get({ [field]: to.params.number }),
          system.events({ [field]: to.params.number }),
          system.contents({ [field]: to.params.number }),
          system.documents({ [field]: to.params.number }),
          system.infoEvents({ [field]: to.params.number }),
        ])
      );
    } catch (e) {
      // try/catch to continue with the execution.
    }

    // Check if events is an array, if it's not, send it as an array to avoid
    // issues while rendering in the component.
    if (events.data) {
      evs = isArray(events.data) ? events.data : [events.data];
    }

    // The next snippet is only for Zenith systems that could have more that one
    // handling unit, for now we only are going to take the first one of every
    // element.
    try {
      if (scope.system === 'Zenith') {
        let handlingUnit = 0;

        ['orderDetails', 'orderHeaders'].filter(k => isArray(details.data[k]))
          .map((k) => {
            [details.data[k]] = details.data[k];
            return details.data[k];
          });

        if (details.data.orderDetails) {
          handlingUnit = details.data.orderDetails.handlingUnitNumber;
        }

        if (handlingUnit) {
          evs = evs.filter(e => e.handlingUnit === handlingUnit);
        }
      }
    } catch (e) {
      console.log(e);
    }


    if (details.data) {
      store.commit(
        'SET_ORDER',
        assign(Parser.map(
          details.data,
          `${scope.system}.Details`,
        ), { uid: uid() }),
      );
      store.commit(
        'SET_ORDER_EVENTS',
        Parser.map(
          evs || [],
          `${scope.system}.Events`,
        ),
      );
      store.commit(
        'SET_ORDER_DOCUMENTS',
        Parser.map(
          documents?.data || [],
          `${scope.system}.Documents`,
        ),
      );
      store.commit(
        'SET_ORDER_CONTENTS',
        Parser.map(
          contents.data,
          `${scope.system}.Contents`,
        ),
      );
      store.commit(
        'SET_ORDER_INFO_EVENTS',
        Parser.map(
          infoEvents.data,
          `${scope.system}.InfoEvents`,
        ),
      );
    }

    // Call IoT after SET_ORDER is done, otherwise the data could be
    // overwritten in a ridiculous but possible scenario, the scenario
    // is: if the request to IoT finishes before the SET_ORDER is done.
    if (canCallIot) {
      try {
        (async () => {
          const { data: hits } = await Iot.hits({ id: to.params.number });
          store.commit(
            'SET_ORDER_HITS',
            hits,
          );
        })();
      } catch (e) {
        console.error(e);
      }
    }

    return { details, events, documents };
  }

  return { };
}

export function getBrowserLang() {
  const langs = ['en', 'fr'];
  const browser = navigator.language.split('-')[0];

  if (langs.includes(browser)) {
    return browser;
  }

  return langs[0];
}
